// @hssna/family-web 首页 (态势概览)
// 规格: SPEC §8.1 family-web 功能规格 / FR-FW-001
//
// 聚合展示:
//   - 欢迎信息 + 当前用户身份
//   - 家庭成员概况 (人数 / 老人数量)
//   - 设备状态概况 (在线 / 离线 / 总数)
//   - 快捷入口卡片 (导航到核心业务页面)
//
// 数据源:
//   - GET /v1/families/{familyId}/members  (S2)
//   - GET /v1/devices?family_group_id={id} (S10)

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Button } from '@hssna/ui-kit';
import { useAuthStore } from '../store/auth-store';
import { apiFetch, getFamilyId, unwrap } from '../lib/api';

type FamilyMember = {
  patientId: string;
  name?: string;
  relationship?: string;
  ageGroup?: string;
  minorFlag?: boolean;
} & Record<string, unknown>;

type Device = {
  id: string;
  status?: 'online' | 'offline' | 'maintenance' | 'error';
  device_type?: string;
} & Record<string, unknown>;

type Alert = {
  id: string;
  status: string;
} & Record<string, unknown>;

// 已解除/关闭状态, 不再视为活跃告警 (与 useAlertStream INACTIVE_STATUSES 保持一致)
const INACTIVE_ALERT_STATUSES: ReadonlySet<string> = new Set([
  'resolved',
  'closed',
  'false-report',
]);

const RELATIONSHIP_LABELS: Record<string, string> = {
  self: '本人',
  spouse: '配偶',
  parent: '父母',
  child: '子女',
  sibling: '兄弟姐妹',
  guardian: '监护人',
};

const AGE_GROUP_LABELS: Record<string, string> = {
  elderly: '老人',
  adult: '成人',
  teen: '青少年',
  child: '儿童',
};

const DEVICE_TYPE_LABELS: Record<string, string> = {
  'wearable-wristband': '智能手环',
  'ecg-patch': '心电贴片',
  'bp-monitor': '血压计',
  'glucose-meter': '血糖仪',
  'spo2-ring': '血氧指环',
  'sleep-mat': '睡眠垫',
  radar: '毫米波雷达',
  'smart-pillbox': '智能药盒',
  'emergency-button': '紧急按钮',
  'smart-scale': '智能体脂秤',
  'environment-sensor': '环境传感器',
};

const ROLE_LABELS: Record<string, string> = {
  family_head: '户主',
  family_member: '家庭成员',
  family_guardian: '家庭监护人',
  practitioner: '医护人员',
  specialist: '专科医生',
  nurse: '护士',
  ops_admin: '运营管理员',
  ops_operator: '运营操作员',
  ems_dispatcher: '急救调度员',
  system_service: '系统服务账号',
};

interface QuickAction {
  to: string;
  label: string;
  desc: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'success' | 'warning';
}

const QUICK_ACTIONS: readonly QuickAction[] = [
  {
    to: '/members',
    label: '家庭成员',
    desc: '管理家庭组内的成员关系',
    icon: '👥',
    variant: 'primary',
  },
  {
    to: '/devices',
    label: '设备管理',
    desc: '绑定与健康监测设备',
    icon: '📱',
    variant: 'secondary',
  },
  {
    to: '/alert-center',
    label: '实时告警',
    desc: '查看活跃告警与升级链',
    icon: '🔔',
    variant: 'warning',
  },
  {
    to: '/health-timeline',
    label: '健康时间线',
    desc: '查看长期健康趋势',
    icon: '📈',
    variant: 'success',
  },
  {
    to: '/consent',
    label: '授权管理',
    desc: '管理数据访问授权',
    icon: '✅',
    variant: 'primary',
  },
  {
    to: '/privacy',
    label: '隐私控制台',
    desc: '隐私保护与数据控制',
    icon: '🔒',
    variant: 'secondary',
  },
];

function formatTimeOfDay(): string {
  const h = new Date().getHours();
  if (h < 6) return '凌晨好';
  if (h < 12) return '上午好';
  if (h < 14) return '中午好';
  if (h < 18) return '下午好';
  return '晚上好';
}

export function HomePage() {
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const familyId = getFamilyId();

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['family-members', familyId],
    queryFn: async () => {
      if (!familyId) return [] as FamilyMember[];
      const res = await apiFetch(`/v1/families/${familyId}/members`);
      const data = await unwrap<FamilyMember[] | { data?: FamilyMember[] }>(res);
      // 兼容 S2 返回 { data: null, meta: {...} } 和 [...] 两种格式
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'data' in data) {
        return (data.data as FamilyMember[]) ?? [];
      }
      return [];
    },
    enabled: !!familyId,
    staleTime: 30_000,
  });

  const { data: devices, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices', familyId],
    queryFn: async () => {
      if (!familyId) return [] as Device[];
      const res = await apiFetch(
        `/v1/devices?family_group_id=${encodeURIComponent(familyId)}`,
      );
      const data = await unwrap<Device[] | { devices?: Device[] }>(res);
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'devices' in data) {
        return (data.devices as Device[]) ?? [];
      }
      return [];
    },
    enabled: !!familyId,
    staleTime: 30_000,
  });

  // 实时告警计数: 拉取告警列表, 过滤出活跃告警 (排除 resolved/closed/false-report)
  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['alerts-active-count', familyId],
    queryFn: async () => {
      if (!familyId) return [] as Alert[];
      const res = await apiFetch(
        `/v1/alerts?family_group_id=${encodeURIComponent(familyId)}&limit=100`,
      );
      const data = await unwrap<Alert[] | { data?: Alert[] } | null>(res);
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'data' in data) {
        return (data.data as Alert[]) ?? [];
      }
      return [];
    },
    enabled: !!familyId,
    staleTime: 30_000,
  });

  const memberList = members ?? [];
  const deviceList = devices ?? [];

  const elderCount = memberList.filter((m) => m.ageGroup === 'elderly').length;
  const onlineDevices = deviceList.filter((d) => d.status === 'online').length;
  const offlineDevices = deviceList.filter(
    (d) => d.status === 'offline' || d.status === 'error',
  ).length;
  const activeAlertCount = (alerts ?? []).filter(
    (a) => !INACTIVE_ALERT_STATUSES.has(a.status),
  ).length;

  const username =
    (typeof user?.username === 'string' ? user.username : null) ??
    user?.sub ??
    '家属';
  const roleLabel = role ? ROLE_LABELS[role] ?? role : '家属成员';

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* 欢迎区域 */}
      <header className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          {formatTimeOfDay()}, {username}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          HSSNA 家庭健康安全态势感知 · {roleLabel}
        </p>
      </header>

      {/* 概况统计卡片 */}
      <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* 家庭成员 */}
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">家庭成员</p>
              <p className="mt-1 text-3xl font-semibold text-gray-900">
                {membersLoading ? '—' : memberList.length}
                <span className="ml-1 text-base font-normal text-gray-400">人</span>
              </p>
            </div>
            <span className="text-3xl" aria-hidden>👥</span>
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
            <span>老人 {elderCount} 人</span>
            <span className="text-gray-300">·</span>
            <span>共 {memberList.length} 人</span>
          </div>
        </div>

        {/* 设备状态 */}
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">监测设备</p>
              <p className="mt-1 text-3xl font-semibold text-gray-900">
                {devicesLoading ? '—' : deviceList.length}
                <span className="ml-1 text-base font-normal text-gray-400">台</span>
              </p>
            </div>
            <span className="text-3xl" aria-hidden>📱</span>
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-health-l1">
              <span className="inline-block h-2 w-2 rounded-full bg-health-l1" />
              在线 {onlineDevices}
            </span>
            <span className="flex items-center gap-1 text-alert-red">
              <span className="inline-block h-2 w-2 rounded-full bg-alert-red" />
              离线 {offlineDevices}
            </span>
          </div>
        </div>

        {/* 告警状态 */}
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">实时告警</p>
              <p className="mt-1 text-3xl font-semibold text-gray-900">
                {alertsLoading ? '—' : activeAlertCount}
              </p>
            </div>
            <span className="text-3xl" aria-hidden>🔔</span>
          </div>
          <div className="mt-3">
            <Link to="/alert-center">
              <Button variant="ghost" size="sm">
                查看告警中心 →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 家庭成员列表预览 */}
      {memberList.length > 0 ? (
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">家庭成员</h2>
            <Link to="/members">
              <Button variant="ghost" size="sm">
                查看全部 →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {memberList.slice(0, 6).map((m) => (
              <div
                key={m.patientId}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                  {RELATIONSHIP_LABELS[m.relationship ?? '']?.charAt(0) ?? '人'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {m.name ?? m.patientId.slice(0, 8)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {RELATIONSHIP_LABELS[m.relationship ?? ''] ?? m.relationship}
                    {m.ageGroup ? ` · ${AGE_GROUP_LABELS[m.ageGroup] ?? m.ageGroup}` : ''}
                  </p>
                </div>
                {m.minorFlag ? (
                  <Badge variant="warning">未成年</Badge>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* 设备列表预览 */}
      {deviceList.length > 0 ? (
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">监测设备</h2>
            <Link to="/devices">
              <Button variant="ghost" size="sm">
                查看全部 →
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {deviceList.slice(0, 6).map((d) => {
              const isOnline = d.status === 'online';
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      isOnline ? 'bg-health-l1/10 text-health-l1' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    📱
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {DEVICE_TYPE_LABELS[d.device_type ?? ''] ?? d.device_type ?? '设备'}
                    </p>
                    <p className="truncate text-xs text-gray-500 font-mono">{d.id.slice(0, 12)}…</p>
                  </div>
                  <Badge variant={isOnline ? 'success' : 'danger'}>
                    {isOnline ? '在线' : '离线'}
                  </Badge>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* 快捷入口 */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">快捷入口</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {QUICK_ACTIONS.map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-brand-primary hover:shadow-md"
            >
              <span className="text-2xl" aria-hidden>{action.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 group-hover:text-brand-primary">
                  {action.label}
                </p>
                <p className="text-xs text-gray-500">{action.desc}</p>
              </div>
              <span className="text-gray-400 group-hover:text-brand-primary">→</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
